import React from 'react';

// Split-flap scoreboard that rolls through the "Win your ___" fills and
// RESTS on the last one. Deliberately does not loop: the rotation is an
// argument that lands on "season.", not decoration.
//
// Visual reference: hand-operated ballpark boards — Wrigley's forest green
// (repainted 1944 to kill sun glare for batters) and Rickwood's numerals
// painted on metal sheets. Colors are inline hex, NOT Tailwind classes,
// because index.css's `.light` class inverts the zinc palette.

const WORDS = ['bracket.', 'parents.', 'bench.', 'bullpen.', 'platoon.', 'season.'];
// The widest fill is 8 characters ("bracket." / "parents." / "platoon."), and
// the board RESTS on "season." forever. At 9 the resting state left a trailing
// empty cell that reads as a rendering fault rather than a design choice, since
// nothing ever fills it again.
const COLS = 8;
const ROLL_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ .';
const HOLD_MS = 1750;                // rest long enough to actually read each one
const FLAP_MS = 78;

// Painted-steel plate faces. Kept a step brighter than the hero's dark
// ground so the board reads as a LIT object hanging in front of the night
// rather than a hole cut into it.
const TOP_BG = 'linear-gradient(180deg, #35634F 0%, #285240 55%, #1E4133 100%)';
const BOTTOM_BG = 'linear-gradient(180deg, #1A392C00 0%, #1B3B2D 6%, #24493A 100%)';
const STENCIL = '#F2EEE4';           // aged enamel, never pure white

interface CellState { top: string; bottom: string; leaf: string | null }
const blank = (): CellState => ({ top: ' ', bottom: ' ', leaf: null });

// The word the board comes to rest on. Its length sets the FINAL cell count:
// while the rotation runs the frame has to be wide enough for the longest fill,
// but once it stops, any leftover blank cells are permanent dead space that
// reads as a rendering fault. Trailing blanks are dropped when it settles.
const FINAL_WORD = WORDS[WORDS.length - 1];
const FINAL_COLS = FINAL_WORD.length;

export const SplitFlapWord: React.FC = () => {
  const [cells, setCells] = React.useState<CellState[]>(
    () => Array.from({ length: COLS }, blank)
  );
  // Once true, the board renders only FINAL_COLS cells.
  const [settled, setSettled] = React.useState(false);
  // Ref mirror of cell chars so the async runner can read current values
  // without re-subscribing to state on every flap.
  const charsRef = React.useRef<string[]>(Array.from({ length: COLS }, () => ' '));
  const cancelled = React.useRef(false);

  React.useEffect(() => {
    cancelled.current = false;

    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const settle = (word: string) => {
      const padded = word.toUpperCase().padEnd(COLS, ' ');
      charsRef.current = padded.split('');
      setCells(padded.split('').map(ch => ({ top: ch, bottom: ch, leaf: null })));
    };

    if (reduce) { settle(FINAL_WORD); setSettled(true); return; }

    const wait = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

    // One flap: a leaf carrying the OLD char falls on its hinge while the
    // NEW char is already waiting behind it.
    const flap = async (i: number, next: string) => {
      const prev = charsRef.current[i];
      setCells(cs => cs.map((c, j) => j === i ? { top: next, bottom: prev, leaf: prev } : c));
      await wait(FLAP_MS);
      if (cancelled.current) return;
      charsRef.current[i] = next;
      setCells(cs => cs.map((c, j) => j === i ? { top: next, bottom: next, leaf: null } : c));
    };

    // Roll one cell to its target through 1–3 intermediates. Restraint is
    // the whole game: a full alphabet roll looks like a slot machine.
    const rollTo = async (i: number, target: string) => {
      if (charsRef.current[i] === target) return;
      const steps = 1 + Math.floor(Math.random() * 3);
      for (let s = 0; s < steps && !cancelled.current; s++) {
        await flap(i, ROLL_CHARS[Math.floor(Math.random() * ROLL_CHARS.length)]);
      }
      if (!cancelled.current) await flap(i, target);
    };

    const show = async (word: string) => {
      const padded = word.toUpperCase().padEnd(COLS, ' ');
      // Left-to-right stagger so the board reads as a wave, not a snap.
      await Promise.all(
        padded.split('').map((ch, i) =>
          wait(i * 26).then(() => (cancelled.current ? undefined : rollTo(i, ch)))
        )
      );
    };

    (async () => {
      for (let w = 0; w < WORDS.length; w++) {
        if (cancelled.current) return;
        await show(WORDS[w]);
        if (w < WORDS.length - 1) await wait(HOLD_MS);
      }
      // Rests on "season." — no loop. Drop the now-permanently-empty cells.
      if (!cancelled.current) setSettled(true);
    })();

    return () => { cancelled.current = true; };
  }, []);

  return (
    <span
      role="img"
      aria-label="Win your season."
      style={{
        display: 'inline-flex',
        gap: 4,
        padding: 12,
        borderRadius: 8,
        background:
          'linear-gradient(180deg, rgba(237,232,220,0.06), transparent 22%),' +
          'linear-gradient(180deg, #2A473A 0%, #1D3529 55%, #16281F 100%)',
        boxShadow:
          'inset 0 1px 0 rgba(237,232,220,0.24),' +
          'inset 0 3px 14px rgba(0,0,0,0.45),' +
          'inset 0 -1px 0 rgba(0,0,0,0.55),' +
          '0 30px 70px -24px rgba(0,0,0,0.92),' +
          '0 0 100px -34px rgba(16,185,129,0.65)',
        maxWidth: '100%',
        overflowX: 'auto',
        verticalAlign: 'middle',
      }}
    >
      {(settled ? cells.slice(0, FINAL_COLS) : cells).map((c, i) => (
        <span key={i} style={cellStyle}>
          {/* lower plate */}
          <span style={{ ...halfStyle, bottom: 0, background: BOTTOM_BG, borderRadius: '0 0 3px 3px', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.55), inset 0 -1px 0 rgba(237,232,220,0.07)' }}>
            <span style={{ ...glyphStyle, bottom: 0 }}>{c.bottom}</span>
          </span>
          {/* upper plate */}
          <span style={{ ...halfStyle, top: 0, background: TOP_BG, borderRadius: '3px 3px 0 0', boxShadow: 'inset 0 1px 0 rgba(237,232,220,0.16), inset 0 -1px 0 rgba(0,0,0,0.5)' }}>
            <span style={{ ...glyphStyle, top: 0 }}>{c.top}</span>
            <span style={hingeStyle} />
          </span>
          {/* falling leaf */}
          {c.leaf !== null && (
            <span
              style={{
                ...halfStyle,
                top: 0,
                background: TOP_BG,
                borderRadius: '3px 3px 0 0',
                boxShadow: 'inset 0 1px 0 rgba(237,232,220,0.16), 0 2px 6px rgba(0,0,0,0.5)',
                transformOrigin: 'bottom center',
                animation: `dugoutFlap ${FLAP_MS}ms cubic-bezier(.4,.05,.6,1) forwards`,
                zIndex: 2,
              }}
            >
              <span style={{ ...glyphStyle, top: 0 }}>{c.leaf}</span>
            </span>
          )}
        </span>
      ))}
      <style>{`@keyframes dugoutFlap{from{transform:rotateX(0)}to{transform:rotateX(-90deg)}}`}</style>
    </span>
  );
};

// clamp() keeps the board proportional from phone to desktop. The glyph
// span must be the FULL cell height and each half clips it — that's what
// splits a painted character across the hinge the way a real flap does.
const CELL_H = 'clamp(38px, 6.4vw, 74px)';
const cellStyle: React.CSSProperties = {
  position: 'relative',
  width: 'clamp(26px, 4.4vw, 50px)',
  height: CELL_H,
  flex: '0 0 auto',
  background: '#12241C',
  borderRadius: 3,
  perspective: 200,
};
const halfStyle: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  height: '50%',
  overflow: 'hidden',
  backfaceVisibility: 'hidden',
};
const glyphStyle: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  height: CELL_H,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
  fontSize: 'clamp(19px, 3.3vw, 38px)',
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  color: STENCIL,
};
const hingeStyle: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: -1,
  height: 2,
  background: 'rgba(0,0,0,0.72)',
};
